import React from "react";
import LegalPage from "../components/LegalPage";

// Privacy Policy — content mirrors terms/Disputefai_Privacy_CLEAN.pdf.
const BLOCKS = [
  { p: "Disputefai respects your privacy. This Privacy Policy explains how we collect, use, store, share, and delete information when you use the Disputefai website, platform, applications, dispute-resolution tools, artificial intelligence features, and related services." },
  { p: "By using Disputefai, you agree to the collection and use of information described in this Privacy Policy. If you do not agree, do not use the Service. Where the law requires a specific legal basis or your consent for certain processing (for example, marketing or non-essential cookies), we rely on the bases described in the Regional Privacy Rights section below." },

  { h: "1. Who We Are" },
  { p: "Disputefai provides an online service that allows users to submit disputes, invite other parties to respond, upload supporting information and evidence, and receive AI-generated analysis, recommendations, or non-binding dispute-resolution assessments." },
  { p: "For purposes of this Privacy Policy, “Disputefai,” “we,” “us,” and “our” refer to TheNextBigWriter, LLC, which is the controller responsible for your personal information." },

  { h: "2. Information We Collect" },
  { p: "We collect information that you provide directly, information generated through your use of the Service, and information from third-party service providers." },
  { sub: "Account Information" },
  { p: "When you create an account or use the Service, we may collect:" },
  { ul: [
    "Name",
    "Email address",
    "Username or account login information",
    "Password or authentication information",
    "Phone number, if provided",
    "Organization name, if applicable",
    "Subscription or account status",
    "Communications with Disputefai",
  ] },
  { sub: "Dispute Information" },
  { p: "When you submit or participate in a dispute, we may collect and process:" },
  { ul: [
    "The title or subject of the dispute",
    "The description of the dispute",
    "Claims, responses, arguments, explanations, and settlement proposals entered by disputants",
    "Statements entered by witnesses or invited participants",
    "Names, contact information, and roles of dispute participants",
    "AI-generated summaries, analysis, recommendations, settlement language, and dispute-resolution outputs",
    "Timestamps, participation history, and dispute status",
    "Records showing whether a dispute was resolved, closed, abandoned, or contested",
  ] },
  { sub: "Evidence and Uploaded Files" },
  { p: "Users may upload evidence to support a dispute. Evidence may include:" },
  { ul: [
    "Documents", "PDFs", "Images", "Screenshots", "Contracts", "Receipts",
    "Messages", "Correspondence", "Photographs", "Other files submitted by users",
  ] },
  { p: "Evidence files are used to analyze the dispute and generate the Disputefai resolution or recommendation." },
  { p: "As described below, Disputefai deletes uploaded evidence files and images 30 days after a resolution has been issued, unless we are legally required to retain them for longer or they are needed to investigate fraud, abuse, security incidents, or violations of our Terms of Service." },
  { sub: "Payment Information" },
  { p: "Disputefai uses Stripe or another third-party payment processor to process payments." },
  { p: "We do not store your full credit card number, debit card number, card security code, or complete payment account credentials on our systems." },
  { p: "Our payment processor may provide us with limited payment-related information, such as:" },
  { ul: [
    "Payment status",
    "Subscription status",
    "Billing email",
    "Billing name",
    "Last four digits of a payment card",
    "Card brand",
    "Expiration month and year",
    "Transaction ID",
    "Invoice history",
    "Refund or chargeback status",
  ] },
  { p: "Payment processing is subject to the privacy policy and terms of our payment processor." },
  { sub: "Technical and Usage Information" },
  { p: "When you use Disputefai, we may collect:" },
  { ul: [
    "IP address", "Browser type", "Device type", "Operating system", "Referring URLs",
    "Pages viewed", "Features used", "Dates and times of access", "Log files", "Error reports",
    "Security and fraud-prevention data", "Approximate location inferred from IP address",
    "Cookies and similar tracking technologies",
  ] },

  { h: "3. How We Use Information" },
  { p: "We use collected information to:" },
  { ul: [
    "Provide, operate, and improve Disputefai",
    "Create and manage user accounts",
    "Process and analyze disputes",
    "Generate AI summaries, analysis, recommendations, and non-binding assessments",
    "Allow disputants and witnesses to submit information",
    "Invite parties to participate in a dispute",
    "Display dispute materials to authorized participants",
    "Store dispute records",
    "Process subscriptions, payments, refunds, and billing issues",
    "Provide customer support",
    "Communicate with users about disputes, accounts, subscriptions, and service updates",
    "Detect fraud, abuse, spam, manipulation, prompt-injection attempts, and security threats",
    "Enforce our Terms of Service",
    "Debug, maintain, and secure the Service",
    "Comply with legal obligations",
    "Protect the rights, safety, and property of Disputefai, users, and others",
  ] },

  { h: "4. How We Use Artificial Intelligence Providers" },
  { p: "Disputefai uses third-party artificial intelligence providers to process dispute submissions and generate outputs." },
  { p: "Information submitted to Disputefai may be sent to AI providers for processing, including:" },
  { ul: [
    "Dispute descriptions",
    "Claims and responses",
    "Witness statements",
    "Uploaded evidence",
    "Extracted text from files or images",
    "Relevant metadata",
    "Prior dispute activity",
    "AI instructions needed to analyze the dispute",
  ] },
  { p: "Where provider controls are available, we configure our AI providers so that customer-submitted dispute information and evidence are not used to train or improve their models." },
  { p: "We also do not use the substance of your dispute submissions or evidence to train Disputefai’s own AI models. Where we say we use information to “improve” the Service, we mean aggregated, de-identified, or operational data (such as usage metrics and error logs) that does not reveal the content of your dispute." },
  { p: "However, AI providers may temporarily process, retain, or log information for technical operation, safety, abuse monitoring, debugging, security, legal compliance, or other purposes described in their commercial terms." },
  { p: "We do not intentionally use free consumer AI tools or free developer tiers for sensitive dispute evidence processing." },
  { sub: "Automated Processing" },
  { p: "Disputefai analyzes disputes and generates its assessments using automated systems, including AI models. These outputs are intended to support—not replace—your own judgment, and they are non-binding unless you separately agree otherwise (for example, community rules that make an outcome binding within a platform)." },

  { h: "5. Data Retention" },
  { p: "Disputefai retains certain dispute records after a resolution has been issued." },
  { p: "Unless you request deletion and we are able to honor that request, we may retain:" },
  { ul: [
    "The dispute title",
    "The dispute description",
    "Claims and responses entered by disputants",
    "Statements entered by witnesses",
    "Settlement terms or resolution descriptions",
    "AI-generated summaries, recommendations, and assessments",
    "Participation records",
    "Timestamps",
    "Account and billing records",
    "Records necessary for security, audit, legal, fraud-prevention, and business purposes",
  ] },
  { p: "We retain these records to preserve the history of the dispute, allow users to access prior resolutions, support account functionality, maintain auditability, prevent abuse, respond to user questions, and comply with legal obligations. We keep each category only for as long as it is needed for these purposes, after which we delete or de-identify it in the ordinary course of our retention practices." },

  { h: "6. Evidence Deletion Policy" },
  { p: "Uploaded evidence, including files and images, is deleted 30 days after a resolution has been issued." },
  { p: "This deletion policy applies to uploaded evidence files such as PDFs, images, screenshots, documents, and similar supporting materials." },
  { p: "After deletion, the evidence files may no longer be available through the Service." },
  { p: "However, deletion may not immediately remove every copy from temporary backups, security logs, technical caches, or third-party provider systems. Backup and technical copies are deleted or overwritten according to our ordinary backup, security, and retention practices." },
  { p: "We may retain evidence longer than 30 days if:" },
  { ul: [
    "Required by law",
    "Necessary to comply with a subpoena, court order, or legal process",
    "Needed to investigate fraud, abuse, threats, harassment, prompt injection, malware, or misuse of the Service",
    "Needed to resolve a billing, security, operational, or legal issue",
    "Needed to protect the rights, safety, or property of Disputefai, users, or others",
  ] },

  { h: "7. Sharing Information Between Dispute Participants" },
  { p: "Disputefai is designed to allow parties to present competing positions in a dispute. Information you submit in a dispute may be shared with other authorized participants in that dispute." },
  { p: "This may include:" },
  { ul: [
    "Your name or display name",
    "Your role in the dispute",
    "Your written statements",
    "Your uploaded evidence before it is deleted",
    "Your settlement proposals",
    "Your responses to other participants",
    "AI-generated summaries or assessments referencing your submissions",
  ] },
  { p: "Do not submit information to a dispute unless you understand that it may be visible to other authorized participants." },
  { sub: "Information About People Who Are Not Account Holders" },
  { p: "Because disputes describe real events, your submissions may contain personal information about people who do not have a Disputefai account—such as the other party, witnesses, or people named in your evidence. When you submit that information, you confirm you have a lawful basis to share it, and we process it to operate the dispute-resolution service on the basis of our and our users’ legitimate interests, or as otherwise permitted by law." },
  { p: "If you are named in someone else’s dispute and want to access, correct, or delete information about you, contact us at contact@disputefai.com. We will consider your request, but our ability to act may be limited because the information forms part of another user’s dispute record, and we may need to consult that user, retain the information to comply with law, or protect the rights of others. You should not submit personal information about a minor unless it is truly necessary to the dispute." },

  { h: "8. Information We Share With Service Providers" },
  { p: "We may share information with vendors and service providers that help us operate Disputefai, including providers of:" },
  { ul: [
    "AI processing", "Cloud hosting", "File storage", "Database services", "Payment processing",
    "Email delivery", "Authentication", "Analytics", "Logging and monitoring", "Customer support",
    "Security and fraud prevention", "Legal and compliance services",
  ] },
  { p: "These providers may access information only as needed to provide services to us, subject to their applicable terms, contracts, and privacy commitments." },

  { h: "9. Payment Processing Through Stripe" },
  { p: "Payments are processed by Stripe or another third-party payment processor." },
  { p: "Disputefai does not store full credit card numbers, debit card numbers, card security codes, or complete payment account credentials." },
  { p: "When you make a payment, your payment information is submitted directly to the payment processor. The payment processor may share limited billing and transaction information with us so that we can manage your subscription, confirm payment, issue refunds, handle chargebacks, and provide customer support." },
  { p: "Your payment information is governed by the payment processor’s own privacy policy and terms." },

  { h: "10. Cookies and Tracking Technologies" },
  { p: "We may use cookies, pixels, local storage, analytics tools, and similar technologies to:" },
  { ul: [
    "Keep you logged in",
    "Remember preferences",
    "Understand how the Service is used",
    "Improve performance",
    "Detect fraud and abuse",
    "Measure marketing effectiveness",
    "Maintain security",
  ] },
  { p: "You may be able to adjust cookie settings through your browser. If you disable cookies, some features may not work properly." },
  { p: "Some cookies are strictly necessary to run the Service; others (such as analytics and advertising cookies) are optional. Where required by law—including for visitors in the EEA and the UK—we ask for your consent before setting non-essential cookies, and you can change your choices at any time through our cookie settings. We honor recognized opt-out preference signals, including Global Privacy Control (GPC), where required." },
  { p: "If we use advertising or marketing trackers that share identifiers with advertising partners, that activity may be considered “selling” or “sharing” of personal information under some U.S. state laws. You can opt out as described in the Regional Privacy Rights section, and we do not use such trackers where the law requires opt-in consent that you have not given. We do not knowingly use advertising trackers to process the substance of your dispute or evidence." },

  { h: "11. Communications" },
  { p: "We may send you emails or messages related to:" },
  { ul: [
    "Account registration", "Login and security", "Dispute invitations", "Dispute activity",
    "Resolutions", "Subscriptions", "Payments", "Policy updates", "Customer support",
    "Product updates", "Marketing, where permitted",
  ] },
  { p: "You may opt out of marketing emails, but we may still send non-marketing messages related to your account, disputes, payments, security, or legal notices. Where we send marketing by email or text message, we do so only with your consent where the law requires it, and you can withdraw that consent at any time." },

  { h: "12. Sensitive Information" },
  { p: "Disputes may involve sensitive facts. You should avoid submitting unnecessary sensitive information." },
  { p: "Do not submit sensitive personal information unless it is directly relevant to the dispute and you have the right to submit it. By choosing to include sensitive information in a dispute, you consent to our processing it for the purpose of operating the dispute-resolution service and generating your assessment, and, where the law requires an additional legal basis or explicit consent for such data, you provide that consent." },
  { p: "Sensitive information may include:" },
  { ul: [
    "Social Security numbers",
    "Government identification numbers",
    "Financial account numbers",
    "Medical records",
    "Mental health records",
    "Information about minors",
    "Intimate images",
    "Immigration documents",
    "Passwords",
    "Trade secrets",
    "Highly confidential business documents",
  ] },
  { p: "We may remove, restrict, or refuse to process certain sensitive information at our discretion." },

  { h: "13. Children’s Privacy" },
  { p: "Disputefai is not intended for children under 18." },
  { p: "We do not knowingly collect personal information from children under 18. If we learn that a child under 18 has provided personal information, we may delete the information and terminate the account." },

  { h: "14. Security" },
  { p: "We use reasonable administrative, technical, and organizational measures designed to protect information from unauthorized access, loss, misuse, alteration, and disclosure." },
  { p: "However, no system is completely secure. We cannot guarantee that information submitted to Disputefai will be perfectly protected." },
  { p: "You are responsible for keeping your account credentials secure and for using the Service responsibly." },

  { h: "15. Your Privacy Choices" },
  { p: "Depending on where you live, you may have rights to:" },
  { ul: [
    "Access personal information we hold about you",
    "Correct inaccurate information",
    "Request deletion of certain information",
    "Object to or restrict certain processing",
    "Request a copy of certain information",
    "Opt out of marketing communications",
    "Close your account",
  ] },
  { p: "To make a privacy request, contact us at contact@disputefai.com." },
  { p: "We may need to verify your identity before responding. We may deny or limit requests where permitted by law, including where information must be retained for legal, security, fraud-prevention, contractual, billing, or dispute-record purposes. If we deny your request, we will explain why, and—where the law provides a right to appeal—you may appeal by contacting us at contact@disputefai.com. You may also authorize someone to make a request on your behalf. We do not charge a fee for most requests or discriminate against you for exercising your rights." },

  { h: "16. Deleting Your Account" },
  { p: "You may request deletion of your account by contacting us at contact@disputefai.com or using account deletion tools if available." },
  { p: "Account deletion may not delete all dispute records immediately. We may retain dispute descriptions, participant statements, witness statements, settlement records, AI-generated outputs, transaction records, and security records where reasonably necessary for legitimate business, legal, audit, fraud-prevention, or dispute-record purposes." },
  { p: "Uploaded evidence files and images are deleted 30 days after a resolution has been issued, subject to the exceptions described in this Privacy Policy." },

  { h: "17. Legal Requests and Safety" },
  { p: "We may access, preserve, or disclose information if we believe it is reasonably necessary to:" },
  { ul: [
    "Comply with law, subpoenas, court orders, or legal process",
    "Respond to lawful requests from public authorities",
    "Enforce our Terms of Service",
    "Investigate fraud, abuse, harassment, threats, or misuse",
    "Detect and prevent security incidents",
    "Protect the rights, safety, and property of Disputefai, users, or others",
    "Prevent harm",
  ] },

  { h: "18. Business Transfers" },
  { p: "If Disputefai is involved in a merger, acquisition, financing, reorganization, bankruptcy, sale of assets, or similar transaction, user information may be transferred as part of that transaction." },
  { p: "If this happens, we will take reasonable steps to require that the transferred information remains subject to privacy protections consistent with this Privacy Policy." },

  { h: "19. International Users and Data Transfers" },
  { p: "Disputefai is operated from the United States. If you access the Service from outside the United States, your information may be processed and stored in the United States or other countries where our service providers operate." },
  { p: "Privacy laws in these countries may differ from those in your location." },
  { p: "Where we transfer personal information from the EEA, the UK, or Switzerland to the United States or another country that has not been recognized as providing adequate protection, we rely on appropriate safeguards, such as the European Commission’s Standard Contractual Clauses (with the UK Addendum where relevant) or another lawful transfer mechanism. You may contact us to ask about these safeguards." },

  { h: "20. Regional Privacy Rights" },
  { p: "This section provides additional information for people in certain regions. If more than one part applies to you, we honor the protections that give you the greater rights." },
  { sub: "EEA, United Kingdom, and Switzerland" },
  { p: "If you are in the EEA, the UK, or Switzerland, we process your personal information under one or more of these legal bases: to perform our contract with you (providing the Service); our legitimate interests (operating, securing, and improving the Service, and analyzing disputes) balanced against your rights; your consent (for example, marketing and non-essential cookies, and for including sensitive information in a dispute); and compliance with legal obligations. You may withdraw consent at any time without affecting prior processing." },
  { p: "You have the rights to access, correct, delete, and receive a portable copy of your personal information; to object to or restrict certain processing; and to withdraw consent. You also have the right to lodge a complaint with your local data protection supervisory authority. TheNextBigWriter, LLC is the controller of your personal information." },
  { sub: "California" },
  { p: "If you are a California resident, you may request access to, correction of, or deletion of your personal information, and request the categories and specific pieces of personal information we have collected, used, disclosed, and shared. We do not sell your personal information for money. If we use advertising trackers as described in the Cookies section, that activity may count as “sharing” for cross-context behavioral advertising; you may opt out by adjusting our cookie settings or by sending a Global Privacy Control (GPC) signal, which we honor." },
  { sub: "Other Regions" },
  { p: "Residents of other jurisdictions—including U.S. states with comprehensive privacy laws and countries such as Canada, Brazil, and Australia—may have similar rights to access, correct, delete, or object to the processing of their personal information. This can all be done online." },

  { h: "21. Changes to This Privacy Policy" },
  { p: "We may update this Privacy Policy from time to time." },
  { p: "If we make material changes, we may notify you by email, through the Service, or by posting an updated version. The “Effective Date” above shows when this Privacy Policy was last updated." },
  { p: "Your continued use of Disputefai after the updated Privacy Policy becomes effective means you accept the updated policy." },

  { h: "22. Contact Us" },
  { p: "If you have questions about this Privacy Policy or want to make a privacy request, contact us at: contact@disputefai.com." },
];

export default function Privacy() {
  return <LegalPage title="Privacy Policy" effectiveDate="July 7, 2026" blocks={BLOCKS} />;
}
